import * as React from 'react';
import Radium from 'radium';
import * as Tgcc from '../tgccGlobal';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import * as tgccRequest from '../tgccRequest';

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
    backgroundColor: '#f8f9fa',
    fontFamily: 'Lulo',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
  },
  logo: {
    width: '120px',
    height: '120px',
    marginBottom: '20px',
  },
  title: {
    textAlign: 'center',
    fontSize: '36px',
    color: '#3A54B4',
    marginBottom: '20px',
  },
  inputGroup: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%', // Ensures inputs don't expand due to labels
    maxWidth: '450px', // Ensures consistency for all input groups
  },
  label: {
    fontSize: '18px',
    marginBottom: '5px',
    color: '#050A5E',
    display: 'block', // Makes label independent from input width
    width: '100%', // Prevents misalignment
  },
  input: {
    padding: '12px',
    width: '100%', // Consistent width
    fontSize: '18px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'BarlowLight',
    boxSizing: 'border-box', // Includes padding within width
  },
  checkbox: {
    marginRight: '10px',
  },
  button: {
    backgroundColor: '#233D92',
    color: 'white',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '30px',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    textTransform: 'uppercase',
    alignSelf: 'center',
    ':hover': {
      backgroundColor: '#1A2E73',
    },
  },
  error: {
    color: 'red',
    marginBottom: '15px',
    fontSize: '16px',
  },
  success: {
    color: 'green',
    marginBottom: '15px',
    fontSize: '16px',
  },
};

interface AccountSetupProps {
  dispatch?: any;
  state: Tgcc.GlobalState;
}

interface AccountSetupState {
  error: string;
  success: boolean;
  tokenStatus: 'valid' | 'expired' | 'used' | 'invalid';
  isSubmitting: boolean;
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  smsAllowed: boolean;
  schoolOrBusiness?: string;
  organization?: string;
  studentGroup?: string;
  group?: string;
  buyer?: string; // 'Yes' | 'No'
  marketingOptIn?: boolean;
}

class _AccountSetup extends React.Component<AccountSetupProps, AccountSetupState> {
  state: AccountSetupState = {
    error: '',
    success: false,
    isSubmitting: false,
    tokenStatus: 'valid',
    firstName: '',
    lastName: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    smsAllowed: true,
    schoolOrBusiness: '',
    organization: '',
    studentGroup: '',
    group: '',
    buyer: 'No',
    marketingOptIn: true,
  };

  componentDidMount() {
    const queryParams = new URLSearchParams(window.location.search);
    const email = queryParams.get('email');
    const token = queryParams.get('token');
    const division = queryParams.get('division');

    if (email && token && division) {
      // Use these parameters to fetch the user info
      tgccRequest.getUserInfoByEmail(
        email,
        (userInfo) => {
          this.setState((prevState) => ({
            ...prevState,
            firstName: userInfo.firstName || '',
            lastName: userInfo.lastName || '',
            address: userInfo.address || '',
            city: userInfo.city || '',
            state: userInfo.state || '',
            zip: userInfo.zip || '',
            phone: userInfo.phone || '',
            smsAllowed: userInfo.smsAllowed || false,
            schoolOrBusiness: userInfo.schoolOrBusiness || '',
            organization: userInfo.organization || '',
            studentGroup: userInfo.studentGroup || '',
            buyer: userInfo.buyer || 'No',
            group: userInfo.group || '',
            marketingOptIn: userInfo.marketingOptIn ?? true,
            success: prevState.success,
            isSubmitting: prevState.isSubmitting,
            error: '',
          }));
        },
        (error) => {
          this.setState((prevState) => ({
            ...prevState,
            error: error || 'Failed to load account information.',
          }));
        }
      );
    } else {
      this.setState((prevState) => ({
        ...prevState,
        error: 'Invalid setup URL. Missing required parameters.',
      }));
    }
  }

  handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.currentTarget;
    const { name, value, type, checked } = target;
    this.setState({ [name]: type === 'checkbox' ? checked : value } as Pick<AccountSetupState, keyof AccountSetupState>);
  };

  handleSubmit = () => {
    const {
      firstName,
      lastName,
      address,
      address2,
      city,
      state,
      zip,
      phone,
      smsAllowed,
      schoolOrBusiness,
      organization,
      studentGroup,
      group,
      buyer,
      marketingOptIn,
    } = this.state;
  
    const queryParams = new URLSearchParams(window.location.search);
    const email = queryParams.get('email');
    const token = queryParams.get('token');
    const division = queryParams.get('division');
  
    if (!email || !token) {
      this.setState((prevState) => ({
        ...prevState,
        error: 'Invalid URL. Missing token or email.',
      }));
      return;
    }
  
    if (!firstName || !lastName || !address || !city || !state || !zip || !phone) {
      this.setState((prevState) => ({
        ...prevState,
        error: 'Please fill in all required fields.',
      }));
      return;
    }
  
    this.setState((prevState) => ({
      ...prevState,
      isSubmitting: true,
      error: '',
      success: false,
    }));
  
    // Construct the request payload
    const payload = {
      email,
      token,
      firstName,
      lastName,
      address1: address,
      address2,
      city,
      state,
      zip,
      phone,
      smsAllowed,
      schoolOrBusiness,
      organization,
      studentGroup,
      group,
      buyer,
      marketingOptIn,
    };
  
    // Call the tgccRequest function
    tgccRequest.completeAccountSetup(
      payload,
      (successMessage) => {
        this.setState((prevState) => ({
          ...prevState,
          success: true,
          error: '',
          isSubmitting: false,
        }));
     // Redirect based on division
        let redirectRoute = Tgcc.Routes.Login; // Default to Login
        if (division === 'Collegiate') {
        redirectRoute = Tgcc.Routes.CampusLogin;
        } else if (division === 'Corporate') {
        redirectRoute = Tgcc.Routes.Login;
        } else if (division === 'Indigo') {
        redirectRoute = Tgcc.Routes.IndigoLogin;
        }

        setTimeout(() => browserHistory.push(redirectRoute), 3000);
      },
      (errorMessage) => {
        this.setState((prevState) => ({
          ...prevState,
          error: errorMessage,
          isSubmitting: false,
        }));
      }
    );
  };
  

  render() {
    const { error, success, isSubmitting, firstName, lastName, address, address2, city, state, zip, phone, smsAllowed } = this.state;

    return (
      <div style={styles.container}>
        <img src={Tgcc.ResolveUrl('~/images/2022Logo.png')} alt="Company Logo" style={styles.logo} />
        <h1 style={styles.title}>Complete Your Account Setup</h1>
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>Account setup completed successfully! Redirecting...</div>}

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="firstName">First Name</label>
          <input
            id="firstName"
            type="text"
            name="firstName"
            placeholder="First Name"
            style={styles.input}
            value={firstName}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="lastName">Last Name</label>
          <input
            id="lastName"
            type="text"
            name="lastName"
            placeholder="Last Name"
            style={styles.input}
            value={lastName}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="address">Address</label>
          <input
            id="address"
            type="text"
            name="address"
            placeholder="Address"
            style={styles.input}
            value={address}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="address2">Address 2 (Optional)</label>
          <input
            id="address2"
            type="text"
            name="address2"
            placeholder="Address 2"
            style={styles.input}
            value={address2}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="city">City</label>
          <input
            id="city"
            type="text"
            name="city"
            placeholder="City"
            style={styles.input}
            value={city}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="state">State</label>
          <input
            id="state"
            type="text"
            name="state"
            placeholder="State"
            style={styles.input}
            value={state}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="zip">Zip Code</label>
          <input
            id="zip"
            type="text"
            name="zip"
            placeholder="Zip Code"
            style={styles.input}
            value={zip}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="text"
            name="phone"
            placeholder="Phone"
            style={styles.input}
            value={phone}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div style={styles.inputGroup}>
          <label>
            <input
              type="checkbox"
              name="smsAllowed"
              style={styles.checkbox}
              checked={smsAllowed}
              onChange={this.handleInputChange}
              disabled={isSubmitting}
            />
            Receive SMS Notifications
          </label>
        </div>

        {/* No password fields in passwordless setup */}

        <button style={styles.button} onClick={this.handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    );
  }
}

const mapStateToProps = (state: Tgcc.GlobalState) => ({ state });
export const AccountSetup = connect(mapStateToProps)(Radium(_AccountSetup));
